import retry from 'retry';
import uri from 'urijs';
import urljoin from 'url-join';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PlayerService } from './playerService';

import { SECOND, SECONDS_PER_MINUTE } from 'Utils/time';
import template from './camera.html';
import _ from 'lodash';

/* @ngInject */
function cameraController(
  $scope,
  $element,
  CameraService,
  appConfig
) {
  const vm = this;

  /* * private variables * */
  const MAX_PLAY_TIME = 3 * SECONDS_PER_MINUTE;
  const DEFAULT_FRAME_RATE = 333;
  const FRAME_RATES = {};
  const useStreamV1 = (/stream(-stg|-dev)?\.petrocloud\.com/).test(appConfig.streamingUrl);
  const player = new PlayerService(useStreamV1);
  const destroy$ = new Subject();

  let frame = null;
  let singleImagePreviewElement = null;
  /** SIP Loading Operation(s) */
  let loading;
  let elWatchDestroy = null;

  /* * public variables * */
  vm.playingVideo = false;
  vm.showLoadingSpinner = true;

  /* * public functions * */
  vm.$postLink = postLink;
  vm.$onChanges = onChanges;
  vm.$onDestroy = destroy;
  vm.playVideo = playVideo;
  vm.stopVideo = stopVideo;
  vm.theaterModeStopVideo = theaterModeStopVideo;

  /**
   * Helper function to set frames-per-second and handle player re-play if player is playing
   *
   * @param {string} id  Camera ID
   * @param {number} [fps] Object containing an FPS
   */
  function configureFps(id, fps) {
    FRAME_RATES[id] = fps ? SECOND / fps : DEFAULT_FRAME_RATE;
    if (vm.activeCameraId === id && vm.playingVideo) {
      resetVideo();
    }
  }

  function postLink() {
    // restart video if active camera changes and get SIP
    elWatchDestroy = $scope.$watch(() => $element.find('.player-frame').get(0), (newElement) => {
      elWatchDestroy();
      frame = newElement;
      frame.addEventListener('load', () => {
        if (vm.showLoadingSpinner) {
          $scope.$apply(() => { vm.showLoadingSpinner = false });
        }
      }, { passive: true });
      player.frame = newElement;

      // Single image preview (SIP)
      singleImagePreviewElement = $element.find('img.single-image-preview').get(0);
      getSip();
      vm.loadingSpinners = true;
    });

    vm.activeCameraStream = vm.activeCameraStream || '1';

    // if we are given fps by parent, use it, otherwise fetch it and restart video if needed
    if (_.has(vm.liveSettings, 'fps')) {
      configureFps(vm.activeCameraId, vm.liveSettings.fps);
    } else if (vm.activeCameraId) {
      retrieveFPS(vm.activeCameraId);
    }

    $scope.$on('streamSettingsUpdated', () => {
      retrieveFPS(vm.activeCameraId);
    });

    player.maxPlayTime = _.toNumber(_.get(vm, ['panel', 'config', 'streamTimeoutOverrideSeconds'], MAX_PLAY_TIME));

    player.streaming$
      .pipe(takeUntil(destroy$))
      .subscribe((isStreaming) => {
        $scope.$applyAsync(() => {
          vm.playingVideo = isStreaming;

          if (_.isFunction(vm.playVideoAction)) {
            vm.playVideoAction({ videoPlaying: vm.playingVideo });
          }
        });
      });
  }

  function onChanges({ activeCameraId: camId, activeCameraStream: streamId, toggleVideoFromOutside, liveSettings }) {
    const fps = _.get(liveSettings, 'currentValue.fps');
    if (fps) {
      configureFps(vm.activeCameraId, fps);
    }
    const playVid = _.get(toggleVideoFromOutside, 'currentValue.playVideo');
    if (playVid) {
      vm.playVideo();
    } else if (playVid === false) {
      vm.stopVideo();
    }
    // restart video if active camera changes and get SIP
    const changed = camId || streamId;
    if (changed && singleImagePreviewElement && !changed.isFirstChange()) {
      retrieveFPS();
      playOrGetSip();
    }
  }

  function destroy() {
    player.destroy();
    loading && loading.stop(); // kill the retry interval if present
    destroy$.next();
    destroy$.complete();
  }

  function retrieveFPS(id = vm.activeCameraId) {
    CameraService.getLiveSettings(id)
      .then(({ data: { fps } }) => {
        configureFps(id, fps);
      })
      .catch(() => {
        configureFps(id);
      });
  }


  function getSip(timestamp) {
    if (!(vm.activeCameraId && singleImagePreviewElement)) {
      return;
    }

    loading = retry.operation({
      forever: true,
      minTimeout: SECOND,
      maxTimeout: SECOND * SECONDS_PER_MINUTE,
      randomize: true,
    });
    loading.attempt((_attemptCount) => {
      singleImagePreviewElement.onerror = (err) => { loading.retry(err) };
      singleImagePreviewElement.src =
        uri(urljoin(appConfig.streamingUrl, 'image', vm.activeCameraId, String(vm.activeCameraStream)))
          .query({
            timestamp: timestamp ? Date.now() : undefined,
            user: appConfig.user.id,
          })
          .toString()
      ;
    });
  }

  function playOrGetSip() {
    if (vm.playingVideo) {
      resetVideo();
    } else {
      getSip();
    }
  }

  function resetVideo() {
    vm.stopVideo();
    vm.playVideo();
  }

  function playVideo() {

    if (_.isFunction(vm.theaterStopVideoAction)) {
      vm.theaterStopVideoAction();
    }
    const source = `${appConfig.streamingUrl}/stream/${vm.activeCameraId
    }/${vm.activeCameraStream}?user=${appConfig.user.id}`;

    player.startStream({
      source,
      interval: FRAME_RATES[vm.activeCameraId] || DEFAULT_FRAME_RATE
    });

  }

  function stopVideo() {
    player.stopStream();

    getSip(true);
    vm.showLoadingSpinner = true;

    if (vm.clearOverlayCtrls) {
      vm.clearOverlayCtrls({ clear: true });
    }
  }

  function theaterModeStopVideo() {
    if (_.isFunction(vm.theaterStopVideoAction)) {
      const stopVid = vm.theaterStopVideoAction();

      if (stopVid) {
        vm.stopVideo();
      }
    } else {
      vm.stopVideo();
    }
  }
}

export default {
  name: 'rvCamera',
  template,
  controllerAs: 'CameraCtrl',
  controller: cameraController,
  bindings: {
    loadingSpinner: '<',
    activeCameraId: '<',
    activeCameraStream: '<',
    toggleVideoFromOutside: '<',
    panel: '<?',
    liveSettings: '<?',
    playVideoAction: '&?',
    clearOverlayCtrls: '&?',
    theaterStopVideoAction: '&?'
  }
};
